/**
 * useAuth Hook
 * Custom hook for authentication operations
 */

import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '../store/hooks';
import {
  login as loginAction,
  logout as logoutAction,
  checkAuth as checkAuthAction,
  selectAuth,
  selectIsAuthenticated,
  selectCurrentUser,
  selectAuthLoading,
  selectAuthError,
  clearError,
} from '../store/slices/authSlice';
import { ROUTES } from '../constants/routes';
import * as permissionUtils from '../lib/permissions';

/**
 * Authentication hook
 * Provides authentication state and methods
 */
export const useAuth = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  
  const auth = useAppSelector(selectAuth);
  const isAuthenticated = useAppSelector(selectIsAuthenticated);
  const user = useAppSelector(selectCurrentUser);
  const isLoading = useAppSelector(selectAuthLoading);
  const error = useAppSelector(selectAuthError);

  /**
   * Login user
   * @param {Object} credentials - Login credentials
   */
  const login = async (credentials) => {
    try {
      const result = await dispatch(loginAction(credentials)).unwrap();
      return result;
    } catch (error) {
      throw error;
    }
  };

  /**
   * Logout user
   */
  const logout = async () => {
    try {
      await dispatch(logoutAction()).unwrap();
      navigate(ROUTES.LOGIN);
    } catch (error) {
      console.error('Logout error:', error);
      // Navigate to login even if logout fails
      navigate(ROUTES.LOGIN);
    }
  };

  /**
   * Check if user is authenticated
   */
  const checkAuth = async () => {
    try {
      await dispatch(checkAuthAction()).unwrap();
    } catch (error) {
      console.error('Auth check failed:', error);
    }
  };

  /**
   * Clear authentication error
   */
  const clearAuthError = () => {
    dispatch(clearError());
  };

  /**
   * Check if user has a specific permission
   * @param {string} permission - Permission to check
   */
  const hasPermission = (permission) => {
    return permissionUtils.hasPermission(user, permission);
  };

  /**
   * Check if user has any of the specified permissions
   * @param {Array<string>} permissions - Permissions to check
   */
  const hasAnyPermission = (permissions) => {
    return permissionUtils.hasAnyPermission(user, permissions);
  };

  /**
   * Check if user has all of the specified permissions
   * @param {Array<string>} permissions - Permissions to check
   */
  const hasAllPermissions = (permissions) => {
    return permissionUtils.hasAllPermissions(user, permissions);
  };

  /**
   * Check if user has a specific role
   * @param {string} role - Role to check
   */
  const hasRole = (role) => {
    return permissionUtils.hasRole(user, role);
  };

  /**
   * Check if user has any of the specified roles
   * @param {Array<string>} roles - Roles to check
   */
  const hasAnyRole = (roles) => {
    return permissionUtils.hasAnyRole(user, roles);
  };

  /**
   * Check if user can access a specific module
   * @param {string} module - Module name
   */
  const canAccessModule = (module) => {
    return permissionUtils.canAccessModule(user, module);
  };

  /**
   * Get all permissions for current user
   */
  const getUserPermissions = () => {
    return permissionUtils.getUserPermissions(user);
  };

  return {
    // State
    auth,
    isAuthenticated,
    user,
    isLoading,
    error,
    
    // Methods
    login,
    logout,
    checkAuth,
    clearAuthError,
    
    // Permission checks
    hasPermission,
    hasAnyPermission,
    hasAllPermissions,
    hasRole,
    hasAnyRole,
    canAccessModule,
    getUserPermissions,
  };
};

export default useAuth;
